import { useQuery } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Stethoscope,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import { ComplianceView } from '../components/compliance-view'
import { useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { TeamYearTable } from '../components/team-year-table'
import { Button } from '../components/ui/button'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { TabNav, tabPillClass } from '../components/ui/tab-nav'
import { api } from '../lib/api'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

type TabId = 'my' | 'team' | 'compliance'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/time-accounts',
  component: TimeAccountsPage,
})

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]

/** Format minutes as hh:mm (e.g. 510 -> "08:30", -90 -> "-01:30") */
function fmtHHMM(minutes: number): string {
  const sign = minutes < 0 ? '-' : ''
  const abs = Math.abs(minutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Format minutes with explicit +/- prefix */
function fmtDiff(minutes: number): string {
  if (minutes === 0) return '±00:00'
  const sign = minutes > 0 ? '+' : ''
  return `${sign}${fmtHHMM(minutes)}`
}

/** Format vacation days in German style — comma decimal, trim trailing zero.
 * 30 → "30", 30.5 → "30,5", 30.0 → "30". */
function fmtVacDays(n: number | null | undefined): string {
  if (n == null) return '–'
  const rounded = Math.round(n * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',')
}

// Persönlicher Kompakt-Modus für die Summary-Cards (Ticket: Jahres-Soll/Ist
// verstecken): Präferenz pro Browser via localStorage — Muster wie
// calendar-info-panel.tsx (TAB_STORAGE_KEY) inkl. try/catch für Private Mode.
const COMPACT_STORAGE_KEY = 'time-accounts:compact-summary'

function loadCompactSummary(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(COMPACT_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function fmtVacDaysSigned(n: number): string {
  if (n === 0) return '0'
  const s = fmtVacDays(Math.abs(n))
  return n > 0 ? `+${s}` : `−${s}`
}

function TimeAccountsPage() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1

  const [tab, setTab] = useState<TabId>('my')
  const [year, setYear] = useState(currentYear)
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined)
  const [showAllHourAdj, setShowAllHourAdj] = useState(false)
  const [showAllVacAdj, setShowAllVacAdj] = useState(false)
  const [compactSummary, setCompactSummary] = useState(loadCompactSummary)

  const toggleCompactSummary = () => {
    setCompactSummary((v) => {
      const next = !v
      try {
        window.localStorage.setItem(COMPACT_STORAGE_KEY, next ? '1' : '0')
      } catch {
        // localStorage gesperrt (Private Mode) — Toggle wirkt dann nur für die Session.
      }
      return next
    })
  }

  // Mobile-Shell (<640): nur Titel oben + Minimal-Bar (☰) — Tabs und
  // Jahr-Navigation bleiben bewusst im Content (Mapping §3).
  useMobilePageTitle('Stundenkonten')

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const user = meData?.user
  const isAdmin = user?.role === 'admin'
  // Load manageable users for admin / supervisor picker
  const { data: usersData } = useQuery({
    queryKey: ['users', 'manageable'],
    queryFn: () => api.listManageableUsers(),
    enabled: !!user,
  })

  // Manager-Status: Admin, Subordinates oder Rolle mit viewTeam-Permission.
  // Schaltet 'team' und 'compliance' frei.
  const manageableUsers = usersData?.users ?? []
  const isManager = isAdmin || manageableUsers.some((u) => u.id !== user?.id)
  const effectiveTab: TabId = !isManager && (tab === 'team' || tab === 'compliance') ? 'my' : tab

  const effectiveUserId = selectedUserId || user?.id
  const { data: accountData, isLoading: accountLoading } = useQuery({
    queryKey: ['time-accounts', effectiveUserId, year],
    queryFn: () => api.getTimeAccounts({ userId: effectiveUserId, year }),
    enabled: !!user && effectiveTab === 'my',
  })

  if (meLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-6 w-6 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    )
  }
  if (!user) return <Navigate to="/login" />

  const data = accountData

  const tabDefs: { id: TabId; label: string; show: boolean }[] = [
    { id: 'my', label: 'Mein Konto', show: true },
    { id: 'team', label: 'Team-Jahresübersicht', show: isManager },
    { id: 'compliance', label: 'Arbeitszeitenprüfung', show: isManager },
  ]

  return (
    <div className="space-y-4">
      {/* Einreihiger Standard-Kopf (Paket C): keep-row — Titel lebt <640 in
          der Shell-Top-Leiste, Tabs + Jahr-◀▶ bleiben sichtbar. */}
      <PageHeader
        title="Stundenkonten"
        phoneMode="keep-row"
        inline={
          <TabNav ariaLabel="Stundenkonten-Bereiche">
            {tabDefs
              .filter((t) => t.show)
              .map((t) => (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={effectiveTab === t.id}
                  onClick={() => setTab(t.id)}
                  className={tabPillClass(effectiveTab === t.id)}
                >
                  {t.label}
                </button>
              ))}
          </TabNav>
        }
        actions={
          effectiveTab !== 'compliance' && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setYear((y) => y - 1)}
                aria-label="Vorheriges Jahr"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium tabular-nums">{year}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setYear((y) => y + 1)}
                disabled={year >= currentYear}
                aria-label="Nächstes Jahr"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              {effectiveTab === 'my' && isAdmin && usersData && (
                <Select
                  value={selectedUserId ?? ''}
                  onChange={(e) => setSelectedUserId(e.target.value || undefined)}
                  className="w-auto min-w-[180px] h-8 text-xs"
                >
                  <option value="">Eigene Daten</option>
                  {usersData.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              )}
            </div>
          )
        }
      />

      {effectiveTab === 'team' && <TeamYearTable year={year} />}
      {effectiveTab === 'compliance' && <ComplianceView />}

      {effectiveTab === 'my' &&
        (accountLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin h-6 w-6 border-2 border-accent border-t-transparent rounded-full" />
          </div>
        ) : data ? (
          <>
            {/* Summary cards — Kompakt-Modus klappt Soll/Ist in eine Subline
                der Saldo-Card (sichtbar, nicht hover-only: Touch-first). */}
            <div className="flex justify-end -mb-1.5">
              <button
                type="button"
                onClick={toggleCompactSummary}
                className="inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
              >
                {compactSummary ? (
                  <>
                    <ChevronDown className="h-3 w-3" /> Soll/Ist als Cards anzeigen
                  </>
                ) : (
                  <>
                    <ChevronUp className="h-3 w-3" /> Soll/Ist einklappen
                  </>
                )}
              </button>
            </div>
            <div className={cn('grid grid-cols-1 gap-3', !compactSummary && 'sm:grid-cols-3')}>
              {!compactSummary && (
                <SummaryCard
                  label="Jahres-Soll"
                  value={fmtHHMM(data.totalTargetMinutes)}
                  icon={<Clock className="h-4 w-4 text-text-tertiary" />}
                />
              )}
              {!compactSummary && (
                <SummaryCard
                  label="Jahres-Ist"
                  value={fmtHHMM(data.totalActualMinutes)}
                  icon={<Clock className="h-4 w-4 text-accent" />}
                />
              )}
              <SummaryCard
                label="Gesamt-Saldo"
                value={fmtDiff(data.totalBalanceMinutes)}
                subline={
                  compactSummary
                    ? `Soll ${fmtHHMM(data.totalTargetMinutes)} · Ist ${fmtHHMM(data.totalActualMinutes)}`
                    : undefined
                }
                title={
                  compactSummary
                    ? `Jahres-Soll ${fmtHHMM(data.totalTargetMinutes)} · Jahres-Ist ${fmtHHMM(data.totalActualMinutes)}`
                    : undefined
                }
                icon={
                  data.totalBalanceMinutes >= 0 ? (
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )
                }
                valueColor={
                  data.totalBalanceMinutes > 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : data.totalBalanceMinutes < 0
                      ? 'text-red-600 dark:text-red-400'
                      : undefined
                }
              />
            </div>

            {data.userName && selectedUserId && (
              <p className="text-sm text-text-secondary">
                Daten für <span className="font-medium text-text-primary">{data.userName}</span>
              </p>
            )}

            {/* Carryover + Adjustments card — collapses to first 3 when long. */}
            {data.adjustments && data.adjustments.length > 0 && (
              <div className="rounded-xl border border-border-subtle bg-bg-overlay/40 p-4">
                <div className="flex items-center gap-2 mb-3">
                  {data.carryoverMinutes >= 0 ? (
                    <ArrowUpRight className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-500" />
                  )}
                  <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
                    Stundenanpassungen
                  </span>
                  <span className="text-xs text-text-tertiary">({data.adjustments.length})</span>
                </div>
                <div className="space-y-1">
                  {(showAllHourAdj ? data.adjustments : data.adjustments.slice(0, 3)).map((a) => {
                    const typeLabel =
                      a.type === 'carryover'
                        ? 'Vortrag'
                        : a.type === 'payout'
                          ? 'Auszahlung'
                          : 'Korrektur'
                    return (
                      <div
                        key={`${a.type}-${a.date}-${a.minutes}`}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-text-tertiary tabular-nums text-xs">{a.date}</span>
                          <span className="text-text-secondary">{typeLabel}</span>
                          {a.description && (
                            <span className="text-text-tertiary text-xs">— {a.description}</span>
                          )}
                        </div>
                        <span
                          className={cn(
                            'tabular-nums font-medium',
                            a.minutes > 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : a.minutes < 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-text-secondary',
                          )}
                        >
                          {fmtDiff(a.minutes)}
                        </span>
                      </div>
                    )
                  })}
                </div>
                {data.adjustments.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllHourAdj((v) => !v)}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
                  >
                    {showAllHourAdj ? (
                      <>
                        <ChevronUp className="h-3 w-3" /> Weniger anzeigen
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" /> {data.adjustments.length - 3} weitere
                        anzeigen
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Vacation card */}
            {data.vacationDaysEntitled != null && (
              <div className="rounded-xl border border-border-subtle bg-bg-overlay/40 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="h-4 w-4 text-text-tertiary" />
                  <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
                    Urlaubskonto
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-text-tertiary">Anspruch</p>
                    <p className="text-lg font-semibold tabular-nums text-text-primary">
                      {fmtVacDays(data.vacationDaysEntitled)} Tage/Jahr
                    </p>
                    {data.vacationAdjustmentDays !== 0 && (
                      <p
                        className={cn(
                          'text-2xs tabular-nums',
                          data.vacationAdjustmentDays > 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-red-600 dark:text-red-400',
                        )}
                      >
                        {fmtVacDaysSigned(data.vacationAdjustmentDays)} Tage Anpassung
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary">Genommen</p>
                    <p className="text-lg font-semibold tabular-nums text-text-primary">
                      {fmtVacDays(data.vacationDaysTaken)} Tage
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary">Geplant</p>
                    <p className="text-lg font-semibold tabular-nums text-text-primary">
                      {fmtVacDays(data.vacationDaysPlanned)} Tage
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary">Verbleibend</p>
                    <p
                      className={cn(
                        'text-lg font-semibold tabular-nums',
                        data.vacationDaysRemaining != null && data.vacationDaysRemaining < 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-emerald-600 dark:text-emerald-400',
                      )}
                    >
                      {fmtVacDays(data.vacationDaysRemaining)} Tage
                    </p>
                  </div>
                </div>

                {/* Sick leave (Entgeltfortzahlung §3 EFZG) — only render when data > 0
                  so users with no sick days don't see a noisy widget. */}
                {data.sickDaysYTD > 0 && (
                  <div className="mt-3 pt-3 border-t border-border-subtle">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <Stethoscope className="h-3.5 w-3.5 text-text-tertiary" />
                        <span className="text-text-secondary">Krankheit YTD</span>
                      </div>
                      <span
                        className={cn(
                          'tabular-nums font-medium inline-flex items-center gap-1.5',
                          data.sickPayoutCapWarning ? 'text-warning' : 'text-text-primary',
                        )}
                      >
                        {data.sickPayoutCapWarning && (
                          <span
                            className="inline-block w-2 h-2 rounded-full bg-danger animate-pulse"
                            title="Lohnfortzahlungs-Grenze"
                          />
                        )}
                        {fmtVacDays(data.sickDaysYTD)} Tage / max 42 Tage Lohnfortzahlung
                      </span>
                    </div>
                    {data.sickPayoutCapWarning && (
                      <p className="mt-1 text-2xs text-warning">
                        Hinweis: Ab 42 Krankheitstagen am Stück endet die gesetzliche
                        Entgeltfortzahlung (§3 EFZG). Aktuell {fmtVacDays(data.sickDaysYTD)} Tage
                        YTD.
                      </p>
                    )}
                  </div>
                )}

                {/* Vacation adjustments breakdown — collapses to first 3 when long. */}
                {data.vacationAdjustments && data.vacationAdjustments.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border-subtle space-y-1">
                    <p className="text-2xs uppercase tracking-wider text-text-tertiary mb-1">
                      Urlaubsanpassungen ({data.vacationAdjustments.length})
                    </p>
                    {(showAllVacAdj
                      ? data.vacationAdjustments
                      : data.vacationAdjustments.slice(0, 3)
                    ).map((v) => {
                      const typeLabel =
                        v.type === 'carryover'
                          ? 'Übertrag'
                          : v.type === 'payout'
                            ? 'Auszahlung'
                            : v.type === 'expiry'
                              ? 'Verfall'
                              : 'Korrektur'
                      return (
                        <div key={v.id} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-text-secondary">{typeLabel}</span>
                            {v.expiresAt && (
                              <span className="text-text-tertiary text-2xs">
                                (verfällt {v.expiresAt})
                              </span>
                            )}
                            {v.description && (
                              <span className="text-text-tertiary">— {v.description}</span>
                            )}
                          </div>
                          <span
                            className={cn(
                              'tabular-nums font-medium',
                              v.days > 0
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : v.days < 0
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-text-secondary',
                            )}
                          >
                            {fmtVacDaysSigned(v.days)} Tage
                          </span>
                        </div>
                      )
                    })}
                    {data.vacationAdjustments.length > 3 && (
                      <button
                        type="button"
                        onClick={() => setShowAllVacAdj((v) => !v)}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
                      >
                        {showAllVacAdj ? (
                          <>
                            <ChevronUp className="h-3 w-3" /> Weniger anzeigen
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3 w-3" />{' '}
                            {data.vacationAdjustments.length - 3} weitere anzeigen
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Monthly view — mobile: stacked cards, sm+: classic table */}
            <div className="sm:hidden space-y-2">
              {data.months.map((m) => {
                const isCurrent = year === currentYear && m.month === currentMonth
                const isFuture =
                  year > currentYear || (year === currentYear && m.month > currentMonth)
                return (
                  <div
                    key={m.month}
                    className={cn(
                      'rounded-lg border bg-bg-overlay/30 p-3',
                      isCurrent && 'border-accent/40 bg-accent/5',
                      isFuture && 'opacity-50',
                      !isCurrent && !isFuture && 'border-border-subtle',
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-text-primary">
                        {isCurrent && (
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent mr-2 align-middle" />
                        )}
                        {MONTH_NAMES[m.month - 1]}
                      </p>
                      <p
                        className={cn(
                          'text-xs tabular-nums font-medium',
                          isFuture
                            ? 'text-text-tertiary'
                            : m.balanceMinutes > 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : m.balanceMinutes < 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-text-secondary',
                        )}
                      >
                        Saldo: {isFuture ? '—' : fmtDiff(m.balanceMinutes)}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <p className="text-2xs uppercase tracking-wider text-text-tertiary">Soll</p>
                        <p className="tabular-nums text-text-secondary">
                          {fmtHHMM(m.targetMinutes)}
                        </p>
                      </div>
                      <div>
                        <p className="text-2xs uppercase tracking-wider text-text-tertiary">Ist</p>
                        <p className="tabular-nums text-text-primary font-medium">
                          {isFuture ? '—' : fmtHHMM(m.actualMinutes)}
                        </p>
                      </div>
                      <div>
                        <p className="text-2xs uppercase tracking-wider text-text-tertiary">
                          Diff.
                        </p>
                        <p
                          className={cn(
                            'tabular-nums font-medium',
                            isFuture
                              ? 'text-text-tertiary'
                              : m.diffMinutes > 0
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : m.diffMinutes < 0
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-text-secondary',
                          )}
                        >
                          {isFuture ? '—' : fmtDiff(m.diffMinutes)}
                        </p>
                      </div>
                    </div>
                    {(m.workDays > 0 || m.absenceDays > 0) && (
                      <p className="mt-2 text-2xs text-text-tertiary tabular-nums">
                        {m.workDays} Arbeitstage
                        {m.absenceDays > 0 && ` · ${m.absenceDays} Abwesenheit`}
                      </p>
                    )}
                  </div>
                )
              })}
              {/* Mobile-only totals row */}
              <div className="rounded-lg border-2 border-border-default bg-bg-overlay/50 p-3">
                <p className="text-sm font-semibold text-text-primary mb-2">Jahresgesamt</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-2xs uppercase tracking-wider text-text-tertiary">Soll</p>
                    <p className="tabular-nums">{fmtHHMM(data.totalTargetMinutes)}</p>
                  </div>
                  <div>
                    <p className="text-2xs uppercase tracking-wider text-text-tertiary">Ist</p>
                    <p className="tabular-nums">{fmtHHMM(data.totalActualMinutes)}</p>
                  </div>
                  <div>
                    <p className="text-2xs uppercase tracking-wider text-text-tertiary">Saldo</p>
                    <p
                      className={cn(
                        'tabular-nums font-semibold',
                        data.totalBalanceMinutes > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : data.totalBalanceMinutes < 0
                            ? 'text-red-600 dark:text-red-400'
                            : '',
                      )}
                    >
                      {fmtDiff(data.totalBalanceMinutes)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly table — sm and up */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-default text-text-tertiary text-xs uppercase tracking-wider">
                    <th className="text-left py-2 px-3 font-medium">Monat</th>
                    <th className="text-right py-2 px-3 font-medium">Arbeitstage</th>
                    <th className="text-right py-2 px-3 font-medium">Abwesenheit</th>
                    <th className="text-right py-2 px-3 font-medium">Soll</th>
                    <th className="text-right py-2 px-3 font-medium">Ist</th>
                    <th className="text-right py-2 px-3 font-medium">Differenz</th>
                    <th className="text-right py-2 px-3 font-medium">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((m) => {
                    const isCurrent = year === currentYear && m.month === currentMonth
                    const isFuture =
                      year > currentYear || (year === currentYear && m.month > currentMonth)

                    return (
                      <tr
                        key={m.month}
                        className={cn(
                          'border-b border-border-subtle transition-colors',
                          isCurrent && 'bg-accent/5 border-accent/20',
                          isFuture && 'opacity-40',
                          !isCurrent && !isFuture && 'hover:bg-bg-overlay/40',
                        )}
                      >
                        <td className="py-2.5 px-3 font-medium text-text-primary">
                          {isCurrent && (
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent mr-2 align-middle" />
                          )}
                          {MONTH_NAMES[m.month - 1]}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-text-secondary">
                          {m.workDays}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-text-secondary">
                          {m.absenceDays > 0 ? m.absenceDays : '—'}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-text-secondary">
                          {fmtHHMM(m.targetMinutes)}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-text-primary font-medium">
                          {isFuture ? '—' : fmtHHMM(m.actualMinutes)}
                        </td>
                        <td
                          className={cn(
                            'py-2.5 px-3 text-right tabular-nums font-medium',
                            isFuture
                              ? 'text-text-tertiary'
                              : m.diffMinutes > 0
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : m.diffMinutes < 0
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-text-secondary',
                          )}
                        >
                          {isFuture ? '—' : fmtDiff(m.diffMinutes)}
                        </td>
                        <td
                          className={cn(
                            'py-2.5 px-3 text-right tabular-nums font-medium',
                            isFuture
                              ? 'text-text-tertiary'
                              : m.balanceMinutes > 0
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : m.balanceMinutes < 0
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-text-secondary',
                          )}
                        >
                          {isFuture ? '—' : fmtDiff(m.balanceMinutes)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border-default font-semibold text-text-primary">
                    <td className="py-2.5 px-3">Jahresgesamt</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {data.months.reduce((s, m) => s + m.workDays, 0)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {data.months.reduce((s, m) => s + m.absenceDays, 0) || '—'}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {fmtHHMM(data.totalTargetMinutes)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {fmtHHMM(data.totalActualMinutes)}
                    </td>
                    <td
                      className={cn(
                        'py-2.5 px-3 text-right tabular-nums',
                        data.totalBalanceMinutes > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : data.totalBalanceMinutes < 0
                            ? 'text-red-600 dark:text-red-400'
                            : '',
                      )}
                    >
                      {fmtDiff(data.totalActualMinutes - data.totalTargetMinutes)}
                    </td>
                    <td
                      className={cn(
                        'py-2.5 px-3 text-right tabular-nums',
                        data.totalBalanceMinutes > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : data.totalBalanceMinutes < 0
                            ? 'text-red-600 dark:text-red-400'
                            : '',
                      )}
                    >
                      {fmtDiff(data.totalBalanceMinutes)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-text-tertiary">
            <Clock className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Keine Daten vorhanden</p>
          </div>
        ))}
    </div>
  )
}

function SummaryCard({
  label,
  value,
  icon,
  valueColor,
  subline,
  title,
}: {
  label: string
  value: string
  icon: React.ReactNode
  valueColor?: string
  /** Dezente Zusatzzeile unter dem Wert — im Kompakt-Modus trägt die
   *  Saldo-Card hier Soll/Ist, damit die Info auch auf Touch (ohne Hover)
   *  sichtbar bleibt; title ist nur der Desktop-Bonus. */
  subline?: string
  title?: string
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-bg-overlay/40 p-4" title={title}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
          {label}
        </span>
      </div>
      <p className={cn('text-2xl font-semibold tabular-nums', valueColor ?? 'text-text-primary')}>
        {value}
      </p>
      {subline && <p className="mt-0.5 text-xs text-text-tertiary tabular-nums">{subline}</p>}
    </div>
  )
}
